// src/components/tutorials/JS_Array_Const.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import OutputBox from "../../../OutputBox";
import TryItButton from "../../../TryItButton";

// 🔹 Main Component
export default function JS_Array_Const() {
  const refs = {
    declaration: useRef(null),
    modification: useRef(null),
    reassignment: useRef(null),
    constScope: useRef(null),
  };

  const scrollTo = (key) => {
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const topics = [
    { key: "declaration", label: "Declaring const Arrays" },
    { key: "modification", label: "Modifying const Arrays" },
    { key: "reassignment", label: "Reassigning const Arrays" },
    { key: "constScope", label: "Const Scope Example" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript const Arrays</h1>
      <p className="tutorial-intro">
        Arrays defined with <b>const</b> cannot be reassigned, but their
        elements can still be changed. Let's explore how it works.
      </p>

      {/* 🔹 Key Points Navigation */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h1 style={{ color: "#075985", fontSize: "2.3rem", margin: 0 }}>
          Array Const Behavior
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click below to explore examples of using <code>const</code> with
          arrays.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1rem",
          }}
        >
          {topics.map((t) => (
            <p key={t.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(t.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#000",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {t.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Declaring const Arrays */}
      <div ref={refs.declaration} className="tutorial-section">
        <h2 className="section-title">1️⃣ Declaring const Arrays</h2>
        <p>
          When you declare an array using{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            const
          </code>
          , you cannot reassign the array variable to a new array.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const fruits = ["apple", "banana", "cherry"];
console.log(fruits);`}
          />
          <TryItButton
            code={`const fruits = ["apple", "banana", "cherry"];
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["apple", "banana", "cherry"]</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Modifying const Arrays */}
      <div ref={refs.modification} className="tutorial-section">
        <h2 className="section-title">2️⃣ Modifying const Arrays</h2>
        <p>
          Even though the array is a constant, its contents can be modified. You
          can add, remove, or change elements.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const fruits = ["apple", "banana", "cherry"];
fruits[0] = "mango";
fruits.push("orange");
console.log(fruits);`}
          />
          <TryItButton
            code={`const fruits = ["apple", "banana", "cherry"];
fruits[0] = "mango";
fruits.push("orange");
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["mango", "banana", "cherry", "orange"]</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Reassigning const Arrays */}
      <div ref={refs.reassignment} className="tutorial-section">
        <h2 className="section-title">3️⃣ Reassigning const Arrays</h2>
        <p>
          You <b>cannot</b> reassign a new array to a variable declared with{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            const
          </code>
          . Doing so will cause an error.
        </p>
        <div
          className="code-demo-box"
          style={{
            border: "2px solid #dc2626",
            background: "rgba(220, 38, 38, 0.08)",
          }}
        >
          <CodeCopyBlock
            code={`const cars = ["Saab", "Volvo", "BMW"];
cars = ["Toyota", "Audi"]; // ❌ Error`}
          />
          <TryItButton
            code={`const cars = ["Saab", "Volvo", "BMW"];
cars = ["Toyota", "Audi"];`}
          />
        </div>
        <OutputBox type="error">
          <pre>❌ TypeError: Assignment to constant variable.</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ Const Scope */}
      <div ref={refs.constScope} className="tutorial-section">
        <h2 className="section-title">4️⃣ Const Scope Example</h2>
        <p>
          Variables declared with{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            const
          </code>{" "}
          are block-scoped. That means they exist only within the block they are
          defined in.
        </p>{" "}
        <div
          className="code-demo-box"
          style={{
            border: "2px solid #dc2626",
            background: "rgba(220, 38, 38, 0.08)",
          }}
        >
          <CodeCopyBlock
            code={`{
  const cars = ["BMW", "Audi"];
  console.log(cars); // works here
}
console.log(cars); // ❌ Error: cars is not defined`}
          />
          <TryItButton
            code={`{
  const cars = ["BMW", "Audi"];
  console.log(cars); // works here
}
console.log(cars); // Error`}
          />
        </div>
        <OutputBox type="error">
          <pre>
            ["BMW", "Audi"]
            {"\n"}❌ ReferenceError: cars is not defined
          </pre>
        </OutputBox>
      </div>
    </div>
  );
}
